import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { ArrowRight, Users } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

export const CTA = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { isDark } = useTheme();

  return (
    <section
      id="cta"
      className={`py-24 sm:py-32 relative overflow-hidden ${isDark ? 'bg-[#050505]' : 'bg-[#F8FAFC]'}`}
      ref={ref}
      data-testid="cta-section"
    >
      {/* Background decorations */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-blue-500/10 blur-3xl" />
        <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-cyan-500/10 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-purple-500/10 blur-3xl" />
      </div>

      <div className="max-w-4xl mx-auto px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          {/* Badge */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={isInView ? { scale: 1, opacity: 1 } : {}}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-8"
          >
            <Users className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-medium text-blue-400">
              Join 50,000+ Early Adopters
            </span>
          </motion.div>

          {/* Headline */}
          <h2 className={`text-3xl sm:text-4xl lg:text-5xl tracking-tight font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Join the{' '}
            <span className="gradient-text">Polk Chain Revolution</span>
          </h2>

          {/* Subtext */}
          <p className={`text-lg max-w-2xl mx-auto mb-12 ${isDark ? 'text-zinc-400' : 'text-slate-600'}`}>
            Be part of the next generation of decentralized finance. Connect, trade, and build on the most advanced blockchain ecosystem.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <motion.button
              whileHover={{ scale: 1.02, boxShadow: '0 0 40px rgba(0,85,255,0.5)' }}
              whileTap={{ scale: 0.98 }}
              className="w-full sm:w-auto flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white rounded-full px-8 py-4 font-semibold transition-all shadow-[0_0_20px_rgba(0,85,255,0.3)]"
              data-testid="cta-get-started-btn"
            >
              Get Started
              <ArrowRight className="w-5 h-5" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`w-full sm:w-auto flex items-center justify-center gap-2 rounded-full px-8 py-4 font-semibold backdrop-blur-md border transition-all ${
                isDark 
                  ? 'bg-white/10 hover:bg-white/20 text-white border-white/20' 
                  : 'bg-black/5 hover:bg-black/10 text-gray-900 border-black/10'
              }`}
              data-testid="cta-join-community-btn"
            >
              <Users className="w-5 h-5" />
              Join Community
            </motion.button>
          </div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="flex flex-wrap justify-center gap-12 mt-16"
          >
            {[
              { value: '$2.5B+', label: 'Total Value Locked' },
              { value: '500K+', label: 'Active Users' },
              { value: '1M+', label: 'Transactions' },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className={`text-3xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {stat.value}
                </div>
                <div className={`text-sm ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>
                  {stat.label}
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
